import { formatClockText } from '../../../../../platform/util/clockSystem';
import { getBookingDisplayClock, getBookingTimezone } from '../../../../../platform/util/bookingCalendarLabel';
import React, { useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import {
  CalendarMonth,
  CheckCircleRounded,
  Event,
  MailOutlineRounded,
} from "@mui/icons-material";
import { format } from "date-fns";
import { formatInTimeZone } from 'date-fns-tz';
import { displayBookingDate, getBookingDisplayCalendar } from '../../../../../platform/util/bookingCalendarLabel';

function ConfirmPage() {
  const { title, timeRange, time, color, businessUrl, userName } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const resolvedColor = useMemo(() => {
    const raw = String(color || "").trim();
    if (!raw) return "#2563eb";
    if (raw.startsWith("hex-")) return `#${raw.slice(4)}`;
    return raw;
  }, [color]);

  const entryFromQuery = new URLSearchParams(location.search).get("entry");
  const entryPath = useMemo(
    () => entryFromQuery || (businessUrl ? `/${businessUrl}` : "/care"),
    [entryFromQuery, businessUrl]
  );

  const formattedDate = useMemo(() => {
    try {
      return displayBookingDate(formatInTimeZone(new Date(time), getBookingTimezone(), 'yyyy-MM-dd'), getBookingDisplayCalendar());
    } catch (_error) {
      return "";
    }
  }, [time, location.search]);

  const detailCards = [
    {
      label: "Appointment",
      value: title,
      icon: (
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: "999px",
            backgroundColor: resolvedColor || "#2563eb",
            boxShadow: `0 0 0 6px ${resolvedColor}22`,
          }}
        />
      ),
    },
    {
      label: "Time",
      value: formatClockText(timeRange, getBookingDisplayClock()),
      icon: <CalendarMonth sx={{ color: "primary.main", fontSize: 18 }} />,
    },
    {
      label: "Date",
      value: formattedDate,
      icon: <Event sx={{ color: "primary.main", fontSize: 18 }} />,
    },
    {
      label: "Timezone",
      value: getBookingTimezone() === 'Africa/Addis_Ababa' ? 'Addis Ababa, Ethiopia (UTC+03:00)' : getBookingTimezone().replaceAll('_', ' '),
      icon: <CalendarMonth sx={{ color: "primary.main", fontSize: 18 }} />,
    },
  ].filter((item) => item.value);

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 140px)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        px: 2,
        pt: { xs: 0.35, md: 0.6 },
        pb: { xs: 8, md: 10 },
        backgroundColor: "background.paper",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 760,
          borderRadius: 4,
          overflow: "hidden",
          border: 1, borderColor: "divider",
          bgcolor: "background.paper",
          color: "text.primary",
          boxShadow: "0 30px 80px rgba(15,31,46,0.14)",
        }}
      >
        <Box
          sx={{
            px: { xs: 2.5, sm: 4 },
            py: { xs: 3, sm: 4 },
            borderBottom: "1px solid rgba(29,78,216,0.08)",
            bgcolor: "background.paper",
            color: "text.primary",
          }}
        >
          <Stack spacing={1.5} alignItems="center" textAlign="center">
            <CheckCircleRounded sx={{ fontSize: 44, color: "success.main" }} />
            <Chip
              label="Booking Confirmed"
              size="small"
              sx={{
                height: 30,
                borderRadius: "999px",
                backgroundColor: "action.selected",
                color: "success.main",
                fontWeight: 700,
                fontSize: "0.74rem",
                letterSpacing: "0.03em",
              }}
            />
            <Typography
              sx={{
                fontSize: { xs: "1.95rem", sm: "2.35rem" },
                fontWeight: 800,
                lineHeight: 1.1,
                color: "text.primary",
              }}
            >
              You&apos;re booked.
            </Typography>
            <Typography
              sx={{
                maxWidth: 520,
                color: "text.secondary",
                fontSize: "1rem",
                lineHeight: 1.75,
              }}
            >
              Your appointment with {userName} has been scheduled successfully. Review the details below and head back when you&apos;re ready.
            </Typography>
          </Stack>
        </Box>

        <Box sx={{ px: { xs: 2.5, sm: 4 }, py: { xs: 2.5, sm: 3.5 } }}>
          <Stack spacing={1.4}>
            {detailCards.map((item) => (
              <Box
                key={item.label}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.4,
                  p: 1.75,
                  borderRadius: 2.75,
                  border: 1, borderColor: "divider",
                  backgroundColor: "background.paper",
                }}
              >
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: 2.25,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "action.selected",
                    flexShrink: 0,
                  }}
                >
                  {item.icon}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      color: "text.secondary",
                      fontWeight: 700,
                      letterSpacing: "0.04em",
                      fontSize: "0.7rem",
                    }}
                  >
                    {item.label}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.4,
                      color: "text.primary",
                      fontSize: "0.96rem",
                      fontWeight: 600,
                      lineHeight: 1.55,
                    }}
                  >
                    {item.value}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>

          <Box
            sx={{
              mt: 2.25,
              p: 2,
              borderRadius: 2.75,
              border: 1, borderColor: "divider",
              backgroundColor: "background.paper",
              display: "flex",
              alignItems: "flex-start",
              gap: 1.3,
            }}
          >
            <MailOutlineRounded sx={{ color: "primary.main", fontSize: 20, mt: 0.15 }} />
            <Box>
              <Typography sx={{ fontWeight: 700, color: "text.primary", fontSize: "0.95rem" }}>
                Confirmation sent
              </Typography>
              <Typography sx={{ mt: 0.35, color: "text.secondary", fontSize: "0.92rem", lineHeight: 1.65 }}>
                A calendar invitation and confirmation email should arrive shortly. If it doesn&apos;t show up, check your spam folder.
              </Typography>
            </Box>
          </Box>

          <Button
            variant="contained"
            fullWidth
            onClick={() => navigate(entryPath, { replace: true })}
            sx={{
              mt: 2.5,
              minHeight: 50,
              borderRadius: 2.5,
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.98rem",
              background: "linear-gradient(135deg, #1d4ed8 0%, #0369a1 100%)",
              boxShadow: "0 18px 32px rgba(29,78,216,0.20)",
              "&:hover": {
                background: "linear-gradient(135deg, #1e40af 0%, #075985 100%)",
                boxShadow: "0 18px 32px rgba(29,78,216,0.24)",
              },
            }}
          >
            Back to Request
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default ConfirmPage;
